<template>
	<div :data-style="radioStyle" class="radio-list">
		<label
			v-for="option in options"
			:key="option.value + option.label"
			class="radio-option"
		>
			<input
				:id="id + '-' + option.value"
				:checked="valueIsChecked(option.value)"
				:disabled="disabled"
				:name="name"
				:value="option.value"
				type="radio"
				@change="handleChange($event, option.value)"
			/>

			<span class="radio-icon"></span>

			<span class="text">
				{{ option.label }}
			</span>
		</label>
	</div>
</template>

<script lang="ts" setup>
	import {computed, useAttrs} from 'vue';
	import type {PropType} from 'vue';
	import {sharedFormElementProps, sharedFormInputProps} from '~/components/forms/sharedFormInputProps';

	const props = defineProps({
		...sharedFormElementProps,
		...sharedFormInputProps,
		modelValue: {
			type: [String, Boolean],
			required: true
		},
		options: {
			type: Array as PropType<{ label: string, value: string | boolean }[]>,
			required: true
		}
	});

	const emit = defineEmits(['update:modelValue']);

	const attrs = useAttrs();

	defineOptions({
		inheritAttrs: false
	});

	const valueIsChecked = (value: string | boolean): boolean => {
		if (typeof props.modelValue === 'boolean' || typeof value === 'boolean') {
			return props.modelValue === value;
		}

		if (value === '') return props.modelValue === '';

		return props.modelValue?.includes(value) ?? false;
	};

	const handleChange = (_event: Event, value: string | boolean) => {
		emit('update:modelValue', value);
	};

	const radioStyle = computed(() => {
		let style = 'normal';

		if (attrs.radioStyle === 'block-items') {
			style = 'block-items';
		}

		return style;
	});

</script>

<style scoped>
	.radio-list {
		display: flex;
		flex-wrap: wrap;
		flex-direction: var(--radio-option-flex-direction, column);
		gap: 0 16px;
	}

	.radio-option {
		--padding-y: var(--radio-option-padding-y, 16px);
		flex: 0 0 auto;
		max-width: 100%;
		display: flex;
		align-items: center;
		gap: 10px;

		padding: var(--padding-y) 8px var(--padding-y) 0;
		margin: 0;
		position: relative;

		--icon-size: .875rem;
		--icon-width: 1em;
		--icon-height: 1em;
	}

	.text {
		flex: 0 1 auto;
		font-weight: 500;
	}

	input {
		font-size: var(--icon-size);
		width: var(--icon-width);
		height: var(--icon-height);
		position: absolute;
		opacity: 0;
		margin: 0;
	}

	.radio-icon {
		font-size: var(--icon-size);
		width: var(--icon-width);
		height: var(--icon-height);
		flex: 0 0 var(--icon-width);

		border-radius: .5em;

		border: var(--border-width) solid var(--border-color);
		background: var(--background-color);

		--background-color: #ffffff;
		--border-color: var(--color-grey-500);
		--border-width: 2px;

		transition: all .1s ease-in-out;
	}

	input:hover ~ .radio-icon {
		--border-color: var(--color-primary-500);
	}

	input:active ~ .radio-icon {
		--border-color: var(--color-primary-600);
	}

	input:checked ~ .radio-icon {
		--border-color: var(--color-primary-500);
		--border-width: 4px;
		--background: #ffffff;
	}

	input:checked:hover ~ .radio-icon {
		--border-color: var(--color-primary-600);
		--border-width: 4px;
		--background: #ffffff;
	}

	input:focus-visible ~ .radio-icon {
		box-shadow: 0 0 0 1px var(--background-color), 0 0 0 3px var(--border-color);
	}

	input:disabled ~ .radio-icon {
		--border-color: var(--color-grey-200);
		--background-color: var(--color-grey-50);
	}

	.radio-list[data-style="block-items"] .radio-option {
		border: 1px solid var(--color-grey-100);
		border-radius: 5px;
		margin: 5px 0 0;
		padding: 16px;
	}

	.radio-list[data-style="block-items"] .radio-option:has(input:checked) {
		background: var(--color-primary-50);
	}
</style>
